import {
  Box,
  Button,
  Checkbox,
  Input,
  Popover,
  Portal,
  Text
} from "@chakra-ui/react"
import { stripHTML } from "@pathwright/blocks-core/src/text"
import RadioButton from "@pathwright/ui/src/components/form/form-radio/RadioButton"
import {
  AugmentationPopover,
  useAugmentationDialog
} from "@pathwright/ux/src/components/augmentation/AugmentationDialog"
import AugmentationPanelSection from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import {
  LIKERT_AUGMENTATION_INPUT_PROPS,
  LIKERT_AUGMENTATION_MENU_CONTENT_PROPS,
  LIKERT_AUGMENTATION_TYPOGRAPHY_CSS
} from "./augmentation-styles"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import {
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react"
import type { LikertScaleOption } from "./types"
import {
  CollapsedAnswerSummary,
  DesktopScaleLabel,
  DesktopScaleLabels,
  DesktopScale as DesktopScaleRoot,
  DesktopScaleSegment,
  DesktopScaleSegments,
  EditSharedScaleButton,
  MobileAnswer,
  MobileAnswers,
  MobileEditScaleTrigger,
  MobileSelectedScale,
  MobileSelectedScaleLabel,
  MobileVerticalScaleSegment
} from "./ui"
import {
  clampNumericValue,
  createLikertScaleOption,
  getNextAvailableNumericValue,
  MAX_NUMERIC_VALUE,
  MAX_SCALE_OPTIONS,
  MIN_NUMERIC_VALUE,
  MIN_SCALE_OPTIONS,
  SCALE_LABEL_MAX_LENGTH,
  updateOrdering
} from "./utils"

const SCALE_POPOVER_POSITIONING = { placement: "bottom" as const }

// Chakra/Ark Presence replays the content enter animation whenever the
// positioner restyles (including after a checkbox toggle). Keep the panel
// still after it is open.
const SCALE_POPOVER_CONTENT_CSS = {
  ...LIKERT_AUGMENTATION_TYPOGRAPHY_CSS,
  animation: "none",
  animationName: "none",
  "&[data-state=open], &[data-state=closed]": {
    animation: "none",
    animationName: "none"
  }
} as const

type ScaleOptionAccessibleLabelArgs = {
  statementLabel: string
  option: LikertScaleOption
  optionIndex: number
  optionCount: number
  showNumbers: boolean
}

export const getScaleOptionAccessibleLabel = ({
  statementLabel,
  option,
  optionIndex,
  optionCount,
  showNumbers
}: ScaleOptionAccessibleLabelArgs): string => {
  const statementText = stripHTML(statementLabel || "").trim()
  const optionLabel = (option.label || "").trim()
  const visibleValue = showNumbers ? String(option.numericValue) : ""
  const responseText = [visibleValue, optionLabel].filter(Boolean).join(", ")
  const accessibleResponse =
    responseText || `Option ${optionIndex + 1} of ${optionCount}`

  return statementText
    ? `${statementText} — ${accessibleResponse}`
    : accessibleResponse
}

export type ScaleProps = {
  statementLabel: string
  scale: LikertScaleOption[]
  showNumbers: boolean
  name: string
  selectedScaleOptionID?: string
  checkedColor?: string
  disabled?: boolean
  showEditTrigger?: boolean
  scaleEditorOpen?: boolean
  onSelect?: (scaleOptionID: string) => void
  onScaleChange?: (scale: LikertScaleOption[]) => void
  onScaleEditorOpenChange?: (open: boolean) => void
  onShowNumbersChange?: (showNumbers: boolean) => void
}

export type DesktopScaleProps = ScaleProps & {
  editMode?: boolean
}

const DesktopScale = ({
  statementLabel,
  scale,
  showNumbers,
  name,
  selectedScaleOptionID,
  checkedColor,
  disabled = false,
  editMode = false,
  showEditTrigger = false,
  scaleEditorOpen,
  onSelect,
  onScaleChange,
  onScaleEditorOpenChange,
  onShowNumbersChange
}: DesktopScaleProps) => {
  const scaleRootRef = useRef<HTMLDivElement>(null)

  const handleRadioKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    optionIndex: number
  ) => {
    if (disabled || !scale.length) return

    let nextIndex: number | undefined
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (optionIndex + 1) % scale.length
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (optionIndex - 1 + scale.length) % scale.length
    } else if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = scale.length - 1
    }

    if (nextIndex === undefined) return

    event.preventDefault()
    const nextOption = scale[nextIndex]
    const radios = scaleRootRef.current?.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]'
    )
    radios?.[nextIndex]?.focus()
    onSelect?.(nextOption.scaleOptionID)
  }

  return (
    <DesktopScaleRoot
      ref={scaleRootRef}
      $scaleCount={scale.length}
      $scaleEditorOpen={scaleEditorOpen}
      $showEditTrigger={showEditTrigger}
    >
      <DesktopScaleSegments className="LikertDesktopScaleVisual">
        {scale.map((option, optionIndex) => {
          const selected = selectedScaleOptionID === option.scaleOptionID
          const accessibleLabel = getScaleOptionAccessibleLabel({
            statementLabel,
            option,
            optionIndex,
            optionCount: scale.length,
            showNumbers
          })

          return (
            <DesktopScaleSegment
              key={option.scaleOptionID}
              $checkedColor={checkedColor}
              $disabled={disabled}
              $editMode={editMode}
              $selected={selected}
            >
              <input
                aria-label={accessibleLabel}
                type="radio"
                name={name}
                value={option.scaleOptionID}
                checked={selected}
                disabled={disabled}
                onChange={() => onSelect?.(option.scaleOptionID)}
                onKeyDown={(event) => handleRadioKeyDown(event, optionIndex)}
              />
              {showNumbers ? (
                <span aria-hidden="true">{option.numericValue}</span>
              ) : null}
            </DesktopScaleSegment>
          )
        })}
      </DesktopScaleSegments>
      <DesktopScaleLabels className="LikertDesktopScaleVisual">
        {scale.map((option) => (
          <DesktopScaleLabel
            key={option.scaleOptionID}
            $selected={selectedScaleOptionID === option.scaleOptionID}
          >
            {option.label || null}
          </DesktopScaleLabel>
        ))}
      </DesktopScaleLabels>
      {showEditTrigger ? (
        <EditSharedScalePopover
          triggerClassName="LikertEditSharedScaleTrigger"
          open={scaleEditorOpen}
          scale={scale}
          showNumbers={showNumbers}
          onScaleChange={onScaleChange}
          onOpenChange={onScaleEditorOpenChange}
          onShowNumbersChange={onShowNumbersChange}
        />
      ) : null}
    </DesktopScaleRoot>
  )
}

export default DesktopScale

const EditSharedScalePopover = ({
  triggerClassName,
  open,
  scale,
  showNumbers,
  onScaleChange,
  onOpenChange,
  onShowNumbersChange
}: {
  triggerClassName?: string
  open?: boolean
  scale: LikertScaleOption[]
  showNumbers: boolean
  onScaleChange?: (scale: LikertScaleOption[]) => void
  onOpenChange?: (open: boolean) => void
  onShowNumbersChange?: (showNumbers: boolean) => void
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const ignoreOutsideUntilRef = useRef(0)

  useEffect(() => {
    if (!open) return

    // Quill/contenteditable keeps focus across the opening click, and Zag
    // treats that leftover focus as "outside" and dismisses. Hold dismiss
    // for a beat and move focus into the panel.
    ignoreOutsideUntilRef.current = Date.now() + 200
    const frame = window.requestAnimationFrame(() => {
      contentRef.current?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  const ignoreOpeningOutsideEvent = (event: { preventDefault: () => void }) => {
    if (Date.now() < ignoreOutsideUntilRef.current) event.preventDefault()
  }

  return (
    <AugmentationPopover
      autoFocus
      lazyMount
      open={open}
      onFocusOutside={(event) => event.preventDefault()}
      onInteractOutside={ignoreOpeningOutsideEvent}
      onPointerDownOutside={ignoreOpeningOutsideEvent}
      onOpenChange={({ open: nextOpen }) => onOpenChange?.(nextOpen)}
      unmountOnExit={false}
      positioning={SCALE_POPOVER_POSITIONING}
    >
      <Popover.Trigger asChild>
        <EditSharedScaleButton
          aria-label="Edit shared scale"
          className={triggerClassName}
          type="button"
        >
          <Pathicon icon="edit" /> Edit shared scale
        </EditSharedScaleButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner css={SCALE_POPOVER_CONTENT_CSS}>
          <Popover.Content
            {...LIKERT_AUGMENTATION_MENU_CONTENT_PROPS}
            ref={contentRef}
            animation="none"
            animationDuration="0s"
            css={SCALE_POPOVER_CONTENT_CSS}
            minW="320px"
            maxW="calc(100vw - 24px)"
            outline="none"
            overflow="visible"
            p="15px"
            tabIndex={-1}
            w="320px"
            zIndex="popover"
            _open={{ animation: "none", animationName: "none" }}
            _closed={{ animation: "none", animationName: "none" }}
          >
            <EditSharedScaleDialog
              scale={scale}
              showNumbers={showNumbers}
              onScaleChange={onScaleChange}
              onShowNumbersChange={onShowNumbersChange}
            />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </AugmentationPopover>
  )
}

const scaleEditFieldProps = {
  ...LIKERT_AUGMENTATION_INPUT_PROPS,
  borderRadius: "10px",
  h: "100%",
  minH: 0,
  px: "6px"
} as const

const scaleNumberInputCss = {
  appearance: "textfield",
  MozAppearance: "textfield",
  boxSizing: "content-box",
  fieldSizing: "content",
  width: "min-content",
  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
    display: "none",
    appearance: "none",
    margin: 0
  }
} as const

const EditSharedScaleDialog = ({
  scale,
  showNumbers,
  onScaleChange,
  onShowNumbersChange
}: {
  scale: LikertScaleOption[]
  showNumbers: boolean
  onScaleChange?: (scale: LikertScaleOption[]) => void
  onShowNumbersChange?: (showNumbers: boolean) => void
}) => {
  const [editingID, setEditingID] = useState<string | null>(null)
  const [editingIsNew, setEditingIsNew] = useState(false)
  const [editingFocusTarget, setEditingFocusTarget] = useState<
    "label" | "number"
  >("label")
  const [draftLabel, setDraftLabel] = useState("")
  const [draftNumericValue, setDraftNumericValue] = useState("")
  const numericValueErrorID = useId()

  const parsedNumericValue = Number(draftNumericValue)
  const hasValidNumericValue =
    draftNumericValue.trim() !== "" && Number.isInteger(parsedNumericValue)
  const hasDuplicateNumericValue =
    hasValidNumericValue &&
    scale.some(
      (option) =>
        option.scaleOptionID !== editingID &&
        option.numericValue === parsedNumericValue
    )
  const canSave = hasValidNumericValue && !hasDuplicateNumericValue

  const startEdit = useCallback(
    (
      option: LikertScaleOption,
      isNew = false,
      focusTarget: "label" | "number" = "label"
    ) => {
      setEditingID(option.scaleOptionID)
      setEditingIsNew(isNew)
      setEditingFocusTarget(focusTarget)
      setDraftLabel(option.label || "")
      setDraftNumericValue(String(option.numericValue))
    },
    []
  )

  const stopEdit = useCallback(() => {
    setEditingID(null)
    setEditingIsNew(false)
    setEditingFocusTarget("label")
    setDraftLabel("")
    setDraftNumericValue("")
  }, [])

  const cancelEdit = useCallback(() => {
    if (editingIsNew && editingID) {
      onScaleChange?.(
        updateOrdering(
          scale.filter((option) => option.scaleOptionID !== editingID)
        )
      )
    }
    stopEdit()
  }, [editingID, editingIsNew, onScaleChange, scale, stopEdit])

  const saveEdit = useCallback(() => {
    if (!editingID || !canSave) return

    onScaleChange?.(
      scale.map((option) =>
        option.scaleOptionID === editingID
          ? {
              ...option,
              label: draftLabel,
              numericValue: clampNumericValue(draftNumericValue)
            }
          : option
      )
    )
    stopEdit()
  }, [
    canSave,
    draftLabel,
    draftNumericValue,
    editingID,
    onScaleChange,
    scale,
    stopEdit
  ])

  const dialogRegistration = useMemo(
    () => ({ close: cancelEdit }),
    [cancelEdit]
  )
  useAugmentationDialog(Boolean(editingID), dialogRegistration)

  const applyOrder = useCallback(
    (orderedKeys: string[]) => {
      const optionByID = new Map(
        scale.map((option) => [option.scaleOptionID, option])
      )

      onScaleChange?.(
        updateOrdering(
          orderedKeys
            .map((key) => optionByID.get(key))
            .filter((option): option is LikertScaleOption => Boolean(option))
        )
      )
    },
    [onScaleChange, scale]
  )

  const handleAdd = () => {
    const option = createLikertScaleOption({
      order: scale.length + 1,
      numericValue: getNextAvailableNumericValue(scale)
    })
    onScaleChange?.(updateOrdering([...scale, option]))
    startEdit(option, true)
  }

  const handleDelete = (scaleOptionID: string) => {
    if (scale.length <= MIN_SCALE_OPTIONS) return
    if (editingID === scaleOptionID) stopEdit()
    onScaleChange?.(
      updateOrdering(
        scale.filter((option) => option.scaleOptionID !== scaleOptionID)
      )
    )
  }

  const items = scale.map((option) => {
    const isEditing = option.scaleOptionID === editingID

    return {
      itemKey: option.scaleOptionID,
      label: option.label || "",
      onClick: option.label?.trim() ? () => startEdit(option) : undefined,
      onDoubleActivate: () => startEdit(option),
      leading: (
        <Button
          aria-label={`Edit scale point value ${option.numericValue}`}
          color="inherit"
          fontFamily="inherit"
          fontSize="inherit"
          fontWeight={600}
          h="auto"
          minH={0}
          minW={0}
          p={0}
          variant="plain"
          onClick={() => startEdit(option, false, "number")}
        >
          {option.numericValue}
        </Button>
      ),
      controls: [
        {
          type: "edit" as const,
          onClick: () => startEdit(option)
        },
        {
          type: "delete" as const,
          disabled: scale.length <= MIN_SCALE_OPTIONS,
          handler: () => handleDelete(option.scaleOptionID)
        }
      ],
      editState: isEditing
        ? {
            leading: (
              <Input
                aria-describedby={
                  hasDuplicateNumericValue ? numericValueErrorID : undefined
                }
                aria-invalid={hasDuplicateNumericValue || undefined}
                aria-label="Scale point value"
                autoComplete="off"
                autoFocus={editingFocusTarget === "number"}
                max={MAX_NUMERIC_VALUE}
                min={MIN_NUMERIC_VALUE}
                boxSizing="border-box"
                step={1}
                textAlign="center"
                type="number"
                value={draftNumericValue}
                onChange={(event) => {
                  const { value } = event.target
                  if (value === "") {
                    setDraftNumericValue("")
                    return
                  }
                  const parsed = Number(value)
                  if (!Number.isInteger(parsed)) {
                    setDraftNumericValue(value)
                    return
                  }
                  setDraftNumericValue(String(clampNumericValue(parsed)))
                }}
                onInvalid={(event) => event.preventDefault()}
                {...scaleEditFieldProps}
                css={{
                  ...scaleNumberInputCss,
                  width: `${Math.max(draftNumericValue.length, 1)}ch`
                }}
              />
            ),
            content: (
              <Box flex="1" minW={0} h="100%">
                <Input
                  aria-label="Scale point label"
                  autoComplete="off"
                  autoFocus={editingFocusTarget === "label"}
                  maxLength={SCALE_LABEL_MAX_LENGTH}
                  placeholder=""
                  value={draftLabel}
                  onChange={(event) => setDraftLabel(event.target.value)}
                  {...scaleEditFieldProps}
                  w="full"
                />
              </Box>
            ),
            onSave: saveEdit,
            onCancel: cancelEdit,
            canSave
          }
        : undefined
    }
  })

  return (
    <AugmentationPanelSection.Content gap="15px">
      <AugmentationPanelSection.Heading>
        Scale Options
      </AugmentationPanelSection.Heading>
      <AugmentationPanelSection.List
        items={items}
        sortProps={{
          animateLayout: false,
          disabled: Boolean(editingID),
          onSort: ({ items: nextItems }) =>
            applyOrder(nextItems.map((item) => item.itemKey)),
          onMove: (moveItem, movedIndex) => {
            const keys = items.map((item) => item.itemKey)
            const fromIndex = keys.indexOf(moveItem.itemKey)
            if (fromIndex < 0 || fromIndex === movedIndex) return
            keys.splice(fromIndex, 1)
            keys.splice(movedIndex, 0, moveItem.itemKey)
            applyOrder(keys)
          }
        }}
        addProps={
          scale.length < MAX_SCALE_OPTIONS && !editingID
            ? {
                itemKey: "add-scale-point",
                label: "Add scale point",
                onClick: handleAdd
              }
            : undefined
        }
      />
      <Box h="17px" overflow="hidden" w="full">
        <Text
          id={numericValueErrorID}
          color="fg.error"
          m={0}
          role={hasDuplicateNumericValue ? "alert" : undefined}
          fontFamily="'Inter', sans-serif"
          fontSize="14px"
          lineHeight="17px"
          visibility={hasDuplicateNumericValue ? "visible" : "hidden"}
          whiteSpace="nowrap"
        >
          Each scale point needs a unique number.
        </Text>
      </Box>
      <Checkbox.Root
        checked={showNumbers}
        cursor="pointer"
        display="flex"
        alignItems="center"
        gap="15px"
        onCheckedChange={({ checked }) =>
          onShowNumbersChange?.(checked === true)
        }
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control
          bg="transparent"
          borderColor="rgba(0, 0, 0, 0.25)"
          borderRadius="4px"
          borderWidth="1.5px"
          boxSize="16px"
          color="white"
          p="2px"
          _checked={{
            bg: "#000",
            borderColor: "#000"
          }}
          _focusVisible={{
            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.25)",
            outline: "none"
          }}
        >
          <Checkbox.Indicator />
        </Checkbox.Control>
        <Checkbox.Label
          m={0}
          fontFamily="'Inter', sans-serif"
          fontSize="14px"
          lineHeight="17px"
        >
          Show numbers to learners
        </Checkbox.Label>
      </Checkbox.Root>
    </AugmentationPanelSection.Content>
  )
}

const LikertRadioButton = RadioButton as ComponentType<{
  children?: ReactNode
  name?: string
  value?: string
  checked?: boolean
  onChange?: () => void
  disabled?: boolean
  style?: CSSProperties
}>

export type MobileScaleProps = ScaleProps

export const MobileScale = ({
  statementLabel,
  scale,
  showNumbers,
  name,
  selectedScaleOptionID,
  disabled = false,
  showEditTrigger = false,
  scaleEditorOpen,
  onSelect,
  onScaleChange,
  onScaleEditorOpenChange,
  onShowNumbersChange
}: MobileScaleProps) => (
  <MobileAnswers>
    {scale.map((option, optionIndex) => {
      const selected = selectedScaleOptionID === option.scaleOptionID
      const accessibleLabel = getScaleOptionAccessibleLabel({
        statementLabel,
        option,
        optionIndex,
        optionCount: scale.length,
        showNumbers
      })

      return (
        <MobileAnswer
          as="div"
          key={option.scaleOptionID}
          $disabled={disabled}
          $selected={selected}
          onClick={() => {
            if (!disabled) onSelect?.(option.scaleOptionID)
          }}
        >
          <MobileVerticalScaleSegment
            aria-hidden="true"
            className="LikertMobileVerticalScaleSegment"
            $first={optionIndex === 0}
            $last={optionIndex === scale.length - 1}
            $selected={selected}
          >
            {showNumbers ? (
              <span className="LikertScaleNumericValue">
                {option.numericValue}
              </span>
            ) : null}
          </MobileVerticalScaleSegment>
          <LikertRadioButton
            name={name}
            value={option.scaleOptionID}
            checked={selected}
            disabled={disabled}
            onChange={() => onSelect?.(option.scaleOptionID)}
          >
            {accessibleLabel}
          </LikertRadioButton>
          <span className="LikertScaleOptionLabel">{option.label}</span>
        </MobileAnswer>
      )
    })}
    {showEditTrigger ? (
      <MobileEditScaleTrigger>
        <EditSharedScalePopover
          open={scaleEditorOpen}
          scale={scale}
          showNumbers={showNumbers}
          onScaleChange={onScaleChange}
          onOpenChange={onScaleEditorOpenChange}
          onShowNumbersChange={onShowNumbersChange}
        />
      </MobileEditScaleTrigger>
    ) : null}
  </MobileAnswers>
)

export const MobileScaleSelection = ({
  statementLabel,
  scale,
  selectedScaleOptionID,
  showNumbers,
  checkedColor
}: {
  statementLabel: string
  scale: LikertScaleOption[]
  selectedScaleOptionID: string
  showNumbers: boolean
  checkedColor?: string
}) => {
  const optionIndex = scale.findIndex(
    (option) => option.scaleOptionID === selectedScaleOptionID
  )
  const selectedOption = scale[optionIndex]

  if (!selectedOption) return null

  return (
    <MobileScaleSummary
      accessibleLabel={getScaleOptionAccessibleLabel({
        statementLabel,
        option: selectedOption,
        optionIndex,
        optionCount: scale.length,
        showNumbers
      })}
      checkedColor={checkedColor}
      scale={scale}
      selectedLabel={selectedOption.label}
      selectedScaleOptionID={selectedScaleOptionID}
      showNumbers={showNumbers}
    />
  )
}

export const MobileScaleSummary = ({
  scale,
  showNumbers,
  selectedScaleOptionID,
  selectedLabel,
  accessibleLabel,
  checkedColor
}: {
  scale: LikertScaleOption[]
  showNumbers: boolean
  selectedScaleOptionID?: string
  selectedLabel?: string
  accessibleLabel?: string
  checkedColor?: string
}) => (
  <CollapsedAnswerSummary as="div" $checkedColor={checkedColor}>
    {accessibleLabel ? (
      <LikertRadioButton checked disabled>
        {accessibleLabel}
      </LikertRadioButton>
    ) : null}
    <MobileSelectedScale
      aria-hidden="true"
      className="LikertMobileSelectedScale"
      $scaleCount={scale.length}
    >
      <DesktopScaleSegments>
        {scale.map((option) => (
          <DesktopScaleSegment
            as="span"
            key={option.scaleOptionID}
            $selected={option.scaleOptionID === selectedScaleOptionID}
          >
            {showNumbers ? <span>{option.numericValue}</span> : null}
          </DesktopScaleSegment>
        ))}
      </DesktopScaleSegments>
    </MobileSelectedScale>
    {selectedLabel ? (
      <MobileSelectedScaleLabel
        className="LikertMobileSelectedScaleLabel"
        title={selectedLabel}
      >
        {selectedLabel}
      </MobileSelectedScaleLabel>
    ) : null}
  </CollapsedAnswerSummary>
)
